import type { PressEvent } from '@react-types/shared';
import { FC } from 'react';
import { useIntl } from 'react-intl';

import { Button, PortalTheme, Tooltip } from '@calm-web/design-system';

import { useAnalytics } from '@/hooks/analytics/useAnalytics';

import messages from './messages';
import { ChevronRightIcon, NumberIcon, StepContainer, StyledNav } from './styles';
import { ButtonWrapperProps, TeamsNavProps } from './types';

const ButtonWrapper: FC<ButtonWrapperProps> = ({
	displayedPageName,
	isActive,
	pageNumber,
	customNavigateTo,
	size = 'md',
	disabled = false,
	isLast = false,
	activePage,
}) => {
	const { formatMessage } = useIntl();
	const { logEvent } = useAnalytics();

	if (disabled) {
		return (
			<Tooltip
				content={formatMessage(messages.navLinkDisabledMessage)}
				placement="bottom"
				backgroundColor={'gray6'}
				textColor="white"
			>
				<Button
					backgroundColor="transparent"
					hideShadow
					textColor={'gray6'}
					size={size}
					aria-disabled
					className="navButton"
					Icon={() => <NumberIcon $isActive={isActive}>{pageNumber}</NumberIcon>}
				>
					<StepContainer>
						{displayedPageName}
						{!isLast && <ChevronRightIcon />}
					</StepContainer>
				</Button>
			</Tooltip>
		);
	}

	const _customNavigateTo = (e: PressEvent): void => {
		logEvent('Teams : Nav Button', {
			location: activePage,
			variant: '7-16-24',
			to_location: displayedPageName,
			action: 'click',
		});
		customNavigateTo(e);
	};

	return (
		<Button
			onPress={_customNavigateTo}
			backgroundColor="transparent"
			hideShadow
			textColor={'gray8'}
			size={size}
			className="navButton"
			Icon={() => <NumberIcon $isActive={isActive}>{pageNumber}</NumberIcon>}
		>
			<StepContainer>
				{displayedPageName}
				{!isLast && <ChevronRightIcon />}
			</StepContainer>
		</Button>
	);
};

const TeamsNav: FC<TeamsNavProps> = ({ activePage, navigateTo, completedStep1, completedStep2 }) => {
	const { formatMessage } = useIntl();
	const isMobile = typeof window !== 'undefined' ? window.innerWidth < PortalTheme.breakpoints.tablet : false;

	if (activePage === '#coveredLives') {
		return null;
	}

	return (
		<StyledNav>
			<ButtonWrapper
				displayedPageName={formatMessage(messages.navInfoLabel)}
				pageNumber="1"
				customNavigateTo={e => navigateTo('info')}
				isActive={activePage === '#info'}
				size={isMobile ? 'sm' : undefined}
				activePage={activePage}
			/>
			<ButtonWrapper
				displayedPageName={formatMessage(messages.navCompanyLabel)}
				pageNumber="2"
				customNavigateTo={e => navigateTo('company')}
				isActive={activePage === '#company'}
				disabled={!completedStep1}
				size={isMobile ? 'sm' : undefined}
				activePage={activePage}
			/>
			<ButtonWrapper
				isLast
				displayedPageName={formatMessage(messages.navPaymentLabel)}
				pageNumber="3"
				customNavigateTo={e => navigateTo('payment')}
				isActive={activePage === '#payment'}
				disabled={!completedStep2}
				size={isMobile ? 'sm' : undefined}
				activePage={activePage}
			/>
		</StyledNav>
	);
};

export default TeamsNav;
